var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useEffect, useContext, useMemo, useCallback, useState } from "react";
import { Outlet, ScrollRestoration, useLocation, useParams } from "react-router-dom";
import { ELanguageDetectionPolicy } from "@sequentech/ui-core";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "./providers/AuthContextProvider";
import { SettingsContext } from "./providers/SettingsContextProvider";
import { ApolloWrapper } from "./providers/ApolloContextProvider";
import { VotingPortalError, VotingPortalErrorType } from "./services/VotingPortalError";
import { useAppDispatch } from "./store/hooks";
import { seedElectionEvent } from "./store/electionEvents/electionEventsSlice";
import { PortalChrome } from "./components/PortalChrome";
import { InvalidLoginHintsError, parseLoginHints, removeLoginHintsFromSearch, routeAcceptsLoginHints, } from "./utils/loginHints";
import { PREVIEW_FILE_KEY } from "./routes/PreviewFromFile";
import { createElectionEventConfigLoader } from "./services/ElectionEventConfig";
var App = function () {
    var navigate = useNavigate();
    var globalSettings = useContext(SettingsContext).globalSettings;
    var location = useLocation();
    var _a = useParams(), tenantId = _a.tenantId, eventId = _a.eventId;
    var _b = useContext(AuthContext), isAuthenticated = _b.isAuthenticated, setTenantEvent = _b.setTenantEvent;
    var dispatch = useAppDispatch();
    var loadElectionEventConfig = useState(createElectionEventConfigLoader)[0];
    var loginHintRequest = useState(function () {
        var acceptsLoginHints = routeAcceptsLoginHints(location.pathname);
        try {
            var parsed = acceptsLoginHints
                ? parseLoginHints(location.search)
                : { hints: {}, remainingSearch: location.search };
            return __assign(__assign({}, parsed), { pathname: location.pathname, hash: location.hash });
        }
        catch (error) {
            if (error instanceof InvalidLoginHintsError) {
                var remainingSearch = removeLoginHintsFromSearch(location.search);
                window.history.replaceState(window.history.state, "", "".concat(location.pathname).concat(remainingSearch).concat(location.hash));
                throw new VotingPortalError(VotingPortalErrorType.INVALID_LOGIN_HINT_PARAMETERS);
            }
            throw error;
        }
    })[0];
    var loginHintsForCurrentRoute = useMemo(function () { return (loginHintRequest.pathname === location.pathname ? loginHintRequest.hints : {}); }, [location.pathname, loginHintRequest]);
    useEffect(function () {
        if (Object.keys(loginHintRequest.hints).length === 0) {
            return;
        }
        // Keep validated hints in memory while removing PII from browser history and redirect URIs.
        navigate({
            pathname: loginHintRequest.pathname,
            search: loginHintRequest.remainingSearch,
            hash: loginHintRequest.hash,
        }, { replace: true });
    }, [loginHintRequest, navigate]);
    useEffect(function () {
        if (location.pathname === "/") {
            throw new VotingPortalError(VotingPortalErrorType.NO_ELECTION_EVENT);
        }
    }, [
        globalSettings.DEFAULT_TENANT_ID,
        globalSettings.DEFAULT_EVENT_ID,
        globalSettings.DISABLE_AUTH,
        navigate,
        location.pathname,
    ]);
    var electionEventConfigUrl = "".concat(globalSettings.PUBLIC_BUCKET_URL, "tenant-").concat(tenantId, "/event-").concat(eventId, "/election_event_config.json");
    // Set up tenant and event in AuthContext on initial load.
    // It is needed to fetch the election event config file from S3
    // and apply the language policy before loading any other data.
    var setupTenantEvent = useCallback(function (isCurrent) { return __awaiter(void 0, void 0, void 0, function () {
        var isRegisterFlow, mode, config, presentation, languageConf, defaultLocale, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!tenantId || !eventId) {
                        return [2 /*return*/];
                    }
                    isRegisterFlow = location.pathname.includes("/enroll");
                    mode = isRegisterFlow ? "register" : "login";
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, loadElectionEventConfig(electionEventConfigUrl)];
                case 2:
                    config = _a.sent();
                    if (!isCurrent()) {
                        return [2 /*return*/];
                    }
                    presentation = config.election_event_presentation;
                    languageConf = presentation === null || presentation === void 0 ? void 0 : presentation.language_conf;
                    // Seed early routes from the public config, but never downgrade a
                    // full query result or frozen preview publication already stored.
                    dispatch(seedElectionEvent({
                        id: config.election_event_id,
                        tenant_id: config.tenant_id,
                        presentation: presentation,
                    }));
                    defaultLocale = (languageConf === null || languageConf === void 0 ? void 0 : languageConf.language_detection_policy) ===
                        ELanguageDetectionPolicy.FORCE_DEFAULT
                        ? languageConf.default_language_code
                        : undefined;
                    setTenantEvent(tenantId, eventId, mode, defaultLocale, loginHintsForCurrentRoute);
                    return [3 /*break*/, 4];
                case 3:
                    error_1 = _a.sent();
                    if (!isCurrent()) {
                        return [2 /*return*/];
                    }
                    console.error("Error loading election event config:", error_1);
                    setTenantEvent(tenantId, eventId, mode, undefined, loginHintsForCurrentRoute);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [
        tenantId,
        eventId,
        electionEventConfigUrl,
        loadElectionEventConfig,
        location.pathname,
        loginHintsForCurrentRoute,
        setTenantEvent,
        dispatch,
    ]);
    useEffect(function () {
        if (isAuthenticated) {
            return;
        }
        var isDemo = sessionStorage.getItem("isDemo");
        if (!globalSettings.DISABLE_AUTH && isDemo) {
            // A preview opened from a file has no bucket coordinates to go back
            // to, so it goes back to the page that holds it. Without this branch
            // it would be sent to `/preview/undefined/undefined/…` and land on a
            // blank screen.
            if (sessionStorage.getItem(PREVIEW_FILE_KEY)) {
                navigate("/preview/file");
                window.location.reload();
                return;
            }
            var areaId = sessionStorage.getItem("areaId");
            var documentId = sessionStorage.getItem("documentId");
            var publicationId = sessionStorage.getItem("publicationId");
            navigate("/preview/".concat(tenantId, "/").concat(documentId, "/").concat(areaId, "/").concat(publicationId));
            window.location.reload();
            return;
        }
        var active = true;
        void setupTenantEvent(function () { return active; });
        return function () {
            active = false;
        };
    }, [isAuthenticated, globalSettings.DISABLE_AUTH, navigate, tenantId, setupTenantEvent]);
    return (_jsx(PortalChrome, { Wrapper: ApolloWrapper, before: _jsx(ScrollRestoration, {}), children: _jsx(Outlet, {}) }));
};
export default App;
